import React, { useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getUserGuide, listUserGuides } from './userGuides';

/**
 * /help/guides/:slug — printable, copy/paste-friendly long-form user guide.
 *
 * Buttons at the top:
 *   📋 Copy all     — copies the full guide as rich text + plain text so it
 *                     can be pasted into Outlook / Word / Gmail with headings
 *                     preserved.
 *   🖨️ Print        — opens the browser print dialog (Save as PDF).
 *   ← Back to Help  — go back to /help.
 *
 * Screenshot placeholders are visible dashed boxes that print and copy as
 * "[Screenshot: caption — hint]" so the recipient knows what to insert.
 */
export default function UserGuidePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const guide = getUserGuide(slug);
  const articleRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const otherGuides = useMemo(
    () => listUserGuides().filter((g) => g.slug !== slug),
    [slug]
  );

  if (!guide) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-2xl font-semibold mb-2">Guide not found</h1>
        <p className="text-sm text-surface-muted mb-4">
          The guide you tried to open does not exist.
        </p>
        <button
          type="button"
          onClick={() => navigate('/help')}
          className="px-3 py-1.5 rounded-thin border border-surface-border text-sm hover:bg-surface-subtle"
        >
          ← Back to Help
        </button>
      </div>
    );
  }

  const handleCopy = async () => {
    if (!articleRef.current) return;
    const html = articleRef.current.innerHTML;
    const text = articleRef.current.innerText;
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      // Fallback: select the article so the user can Ctrl+C.
      const range = document.createRange();
      range.selectNodeContents(articleRef.current);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      // eslint-disable-next-line no-alert
      alert('Auto-copy was blocked. The text is now selected — press Ctrl+C / ⌘+C to copy.');
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className="p-6 max-w-4xl mx-auto cx-user-guide">
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-4 no-print">
        <button
          type="button"
          onClick={() => navigate('/help')}
          className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
        >
          ← Back to Help
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleCopy}
          className="px-3 py-1.5 rounded-thin border border-continuum-500 text-sm bg-continuum-500/10 hover:bg-continuum-500/20"
          title="Copy the full guide so you can paste it into an email or Word"
        >
          {copied ? '✓ Copied' : '📋 Copy all'}
        </button>
        <button
          type="button"
          onClick={handlePrint}
          className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          title="Open the browser print dialog (you can also Save as PDF)"
        >
          🖨️ Print / Save PDF
        </button>
      </div>

      {/* Printable article */}
      <article ref={articleRef} className="cx-user-guide__article">
        <header className="mb-6">
          <h1 className="text-3xl font-semibold">{guide.title}</h1>
          {guide.subtitle ? (
            <p className="text-sm text-surface-muted mt-2">{guide.subtitle}</p>
          ) : null}
          <p className="text-xs text-surface-muted mt-2">
            ContinuumCX · cx.continuumgbl.com
          </p>
        </header>

        {/* TOC */}
        <nav className="mb-6 border-l-2 border-surface-border dark:border-surface-dark-border pl-3">
          <div className="text-xs uppercase tracking-wider text-surface-muted mb-1">
            Contents
          </div>
          <ol className="text-sm list-decimal list-inside space-y-0.5">
            {guide.sections.map((s, i) => (
              <li key={i}>{s.heading}</li>
            ))}
          </ol>
        </nav>

        {guide.sections.map((section, i) => (
          <section key={i} className="mb-6">
            <h2 className="text-xl font-semibold mb-2">{section.heading}</h2>
            {section.body.map((block, j) => (
              <Block key={j} block={block} />
            ))}
          </section>
        ))}

        <footer className="mt-10 pt-4 border-t border-surface-border dark:border-surface-dark-border text-xs text-surface-muted">
          ContinuumCX — {guide.title} · Generated from the in-app help system.
        </footer>
      </article>

      {/* Cross-links */}
      {otherGuides.length > 0 ? (
        <div className="mt-8 no-print">
          <div className="text-xs uppercase tracking-wider text-surface-muted mb-2">
            Other guides
          </div>
          <div className="flex flex-wrap gap-2">
            {otherGuides.map((g) => (
              <Link
                key={g.slug}
                to={`/help/guides/${g.slug}`}
                className="text-sm px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border hover:border-continuum-500 hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
              >
                {g.title} →
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Block({ block }) {
  switch (block.type) {
    case 'p':
      return <p className="mb-2 leading-relaxed">{block.text}</p>;
    case 'h3':
      return <h3 className="text-base font-semibold mt-3 mb-1">{block.text}</h3>;
    case 'list':
      return (
        <ul className="mb-2 list-disc pl-6 space-y-0.5">
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    case 'steps':
      return (
        <ol className="mb-2 list-decimal pl-6 space-y-0.5">
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      );
    case 'tip':
      return (
        <div className="my-2 p-2 rounded-thin border-l-4 border-continuum-500 bg-continuum-500/5 text-sm">
          <strong>💡 Tip — </strong>{block.text}
        </div>
      );
    case 'warn':
      return (
        <div className="my-2 p-2 rounded-thin border-l-4 border-amber-500 bg-amber-500/5 text-sm">
          <strong>⚠️ Warning — </strong>{block.text}
        </div>
      );
    case 'kbd':
      return (
        <p className="mb-2">
          <kbd className="px-1.5 py-0.5 rounded border border-surface-border text-xs font-mono">
            {block.text}
          </kbd>
        </p>
      );
    case 'screenshot':
      return (
        <figure className="my-3 border-2 border-dashed border-surface-border dark:border-surface-dark-border rounded-thin p-4 text-center bg-surface-subtle/40 dark:bg-surface-dark-subtle/40">
          <div className="text-2xl">🖼️</div>
          <div className="mt-1 text-sm font-medium">
            [Screenshot: {block.caption}]
          </div>
          {block.hint ? (
            <div className="text-xs text-surface-muted mt-1 italic">
              {block.hint}
            </div>
          ) : null}
        </figure>
      );
    default:
      return null;
  }
}
